import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import StatusBadge from "@/components/StatusBadge";
import OutreachHealthBadge from "@/components/OutreachHealthBadge";
import PriorityBar from "@/components/PriorityBar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, Phone, MapPin, Calendar, Save, Building2, AlertCircle, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate as fmtDateLocal } from "@/lib/formatDate";

const STATUSES = ["Target", "Outreach", "Connected", "Active", "Nurture"];
const TIERS = [1, 2, 3];

export default function BrokerDetailSheet({ open, onOpenChange, broker, onUpdated, onDeleted }) {
    const [activities, setActivities] = useState([]);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (broker?.id && open) {
            setEditing({ ...broker });
            api.get(`/activities?broker_id=${broker.id}`).then((r) => setActivities(r.data)).catch(() => {});
        }
    }, [broker, open]);

    if (!editing) return null;

    const onSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/brokers/${editing.id}`, {
                tier: parseInt(editing.tier, 10),
                status: editing.status,
                notes: editing.notes,
                phone: editing.phone,
                email: editing.email,
                location: editing.location,
                next_action: editing.next_action,
                next_followup_due: editing.next_followup_due,
                estimated_book_size: parseFloat(editing.estimated_book_size) || 0,
                estimated_annual_premium: parseFloat(editing.estimated_annual_premium) || 0,
                submission_count: parseInt(editing.submission_count, 10) || 0,
                current_markets: editing.current_markets,
                owner: editing.owner,
            });
            toast.success("Broker updated");
            onUpdated?.(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/brokers/${editing.id}`);
            toast.success(`${editing.agency_name} deleted`);
            setConfirmDelete(false);
            onOpenChange(false);
            onDeleted?.(editing.id);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    const fmtDate = (d) => fmtDateLocal(d);

    const fmtMoney = (n) => !n ? "—" : (n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-xl overflow-y-auto scrollbar-thin bg-white border-l border-ph-border p-0"
                data-testid="broker-detail-sheet"
            >
                {/* Header section */}
                <div className="px-6 pt-6 pb-4 border-b border-ph-border">
                    <SheetHeader>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-ph-primarySoft flex items-center justify-center shrink-0">
                                <Building2 className="w-5 h-5 text-ph-primary" strokeWidth={2} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <SheetTitle className="text-lg font-semibold text-ph-text leading-tight">
                                    {editing.agency_name}
                                </SheetTitle>
                                <SheetDescription className="text-sm text-ph-muted mt-0.5">
                                    {editing.producer_name} · {editing.location || "Location not set"}
                                </SheetDescription>
                            </div>
                        </div>
                    </SheetHeader>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <StatusBadge status={editing.status} />
                        <OutreachHealthBadge value={editing.outreach_health} />
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-ph-canvas border border-ph-border text-ph-muted">
                            Tier {editing.tier}
                        </span>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Underwriting flags */}
                    <section className="rounded-lg border border-ph-border bg-ph-canvas p-4 flex items-start gap-3">
                        <AlertCircle className="w-4 h-4 text-ph-muted mt-0.5" strokeWidth={2} />
                        <div className="flex-1">
                            <div className="text-sm font-medium text-ph-text">No underwriting flags</div>
                            <div className="text-xs text-ph-muted mt-0.5">Risks, recommendations, and AI insights will appear here.</div>
                        </div>
                    </section>

                    {/* Contact info */}
                    <section className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        <div>
                            <div className="ph-label">Email</div>
                            <a href={`mailto:${editing.email}`} className="text-ph-primary hover:underline mt-0.5 block truncate">
                                {editing.email || "—"}
                            </a>
                        </div>
                        <div>
                            <div className="ph-label">Phone</div>
                            <div className="ph-value mt-0.5">{editing.phone || "—"}</div>
                        </div>
                        <div>
                            <div className="ph-label">Location</div>
                            <div className="ph-value mt-0.5">{editing.location || "—"}</div>
                        </div>
                        <div>
                            <div className="ph-label">Current markets</div>
                            <div className="ph-value mt-0.5 truncate">{editing.current_markets || "—"}</div>
                        </div>
                    </section>

                    {/* Metrics */}
                    <section className="grid grid-cols-3 gap-3">
                        <div className="ph-card p-3">
                            <div className="ph-label">Priority</div>
                            <div className="mt-1.5"><PriorityBar score={editing.priority_score || 0} /></div>
                        </div>
                        <div className="ph-card p-3">
                            <div className="ph-label">Days since contact</div>
                            <div className="text-xl font-semibold text-ph-text mt-1 tabular-nums">
                                {editing.days_since_last_contact ?? "—"}
                            </div>
                        </div>
                        <div className="ph-card p-3">
                            <div className="ph-label">Submissions</div>
                            <div className="text-xl font-semibold text-ph-text mt-1 tabular-nums">
                                {editing.submission_count || 0}
                            </div>
                        </div>
                        <div className="ph-card p-3">
                            <div className="ph-label">Annual premium</div>
                            <div className="text-base font-semibold text-ph-text mt-1 tabular-nums">{fmtMoney(editing.estimated_annual_premium)}</div>
                        </div>
                        <div className="ph-card p-3">
                            <div className="ph-label">Book size</div>
                            <div className="text-base font-semibold text-ph-text mt-1 tabular-nums">{fmtMoney(editing.estimated_book_size)}</div>
                        </div>
                        <div className="ph-card p-3">
                            <div className="ph-label">Next follow-up</div>
                            <div className="text-sm font-semibold text-ph-text mt-1">{fmtDate(editing.next_followup_due)}</div>
                        </div>
                    </section>

                    {/* Milestone timeline */}
                    <section>
                        <h3 className="ph-h2 mb-3">Milestones</h3>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                            {[
                                ["Added", editing.date_added],
                                ["Connected", editing.date_connected],
                                ["Activated", editing.date_activated],
                            ].map(([label, val]) => (
                                <div key={label} className="rounded-lg border border-ph-borderSoft p-3 bg-white">
                                    <div className="ph-label">{label}</div>
                                    <div className="text-sm font-medium text-ph-text mt-0.5 flex items-center gap-1">
                                        <Calendar className="w-3 h-3 text-ph-mutedSoft" /> {fmtDate(val)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Edit fields */}
                    <section>
                        <h3 className="ph-h2 mb-3">Edit broker</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="ph-label mb-1 block">Status</label>
                                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                                    <SelectTrigger data-testid="edit-status-trigger"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="ph-label mb-1 block">Tier</label>
                                <Select value={String(editing.tier)} onValueChange={(v) => setEditing({ ...editing, tier: parseInt(v, 10) })}>
                                    <SelectTrigger data-testid="edit-tier-trigger"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TIERS.map((t) => <SelectItem key={t} value={String(t)}>Tier {t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="ph-label mb-1 block">Phone</label>
                                <Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="edit-phone-input" />
                            </div>
                            <div>
                                <label className="ph-label mb-1 block">Location</label>
                                <Input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} data-testid="edit-location-input" />
                            </div>
                            <div>
                                <label className="ph-label mb-1 block">Annual premium ($)</label>
                                <Input type="number" value={editing.estimated_annual_premium || 0} onChange={(e) => setEditing({ ...editing, estimated_annual_premium: e.target.value })} data-testid="edit-premium-input" />
                            </div>
                            <div>
                                <label className="ph-label mb-1 block">Submissions</label>
                                <Input type="number" value={editing.submission_count || 0} onChange={(e) => setEditing({ ...editing, submission_count: e.target.value })} data-testid="edit-submissions-input" />
                            </div>
                            <div className="col-span-2">
                                <label className="ph-label mb-1 block">Owner</label>
                                <Input
                                    value={editing.owner || ""}
                                    onChange={(e) => setEditing({ ...editing, owner: e.target.value })}
                                    placeholder="Who on the BD team owns this? e.g. Matt, Jay"
                                    data-testid="edit-owner-input"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="ph-label mb-1 block">Notes</label>
                                <Textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={3} data-testid="edit-notes-input" />
                            </div>
                        </div>
                        <Button onClick={onSave} disabled={saving} className="w-full mt-4 bg-ph-primary text-white font-medium hover:bg-ph-primaryHover" data-testid="save-broker-btn">
                            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </section>

                    {/* Activity history */}
                    <section>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="ph-h2">Activity history</h3>
                            <span className="text-xs text-ph-muted">{activities.length} {activities.length === 1 ? "touch" : "touches"}</span>
                        </div>
                        {activities.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-ph-border p-6 text-center text-sm text-ph-muted">
                                No touches logged yet for this broker.
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
                                {activities.map((a) => (
                                    <div key={a.id} className="rounded-lg border border-ph-borderSoft p-3 hover:bg-ph-canvas transition-colors" data-testid={`activity-row-${a.id}`}>
                                        <div className="flex justify-between items-start gap-3">
                                            <div className="text-sm font-medium text-ph-text">{a.activity_type}</div>
                                            <div className="text-[11px] text-ph-muted whitespace-nowrap">{fmtDate(a.activity_date)}</div>
                                        </div>
                                        <div className="text-xs text-ph-muted mt-0.5">{a.channel} · {a.outcome}</div>
                                        {a.notes && <div className="text-xs mt-1.5 text-ph-text">{a.notes}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Danger zone */}
                    <section className="pt-2 mt-4 border-t border-ph-borderSoft">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-ph-muted mb-2">Danger zone</h3>
                        <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4 flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-ph-text">Delete this broker</div>
                                <p className="text-xs text-ph-muted mt-0.5">
                                    Permanently removes the agency and every activity ever logged against them. Use this when a broker has sold to another agency or you no longer pursue them.
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setConfirmDelete(true)}
                                className="border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800 shrink-0"
                                data-testid="delete-broker-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                            </Button>
                        </div>
                    </section>
                </div>
            </SheetContent>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent data-testid="delete-broker-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {editing.agency_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the broker and all {activities.length} {activities.length === 1 ? "activity" : "activities"} logged against them. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-broker-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={deleting}
                            className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                            data-testid="delete-broker-confirm-btn"
                        >
                            {deleting ? "Deleting…" : "Delete broker"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Sheet>
    );
}
